import {
  Form,
  Radio,
  Space,
  Input,
  DatePicker,
  RadioChangeEvent,
  Select,
} from "antd";
import moment from "moment";
import { ReleaseTypeEnum } from "shared/enums/ReleaseTypeEnum";
import { ReactComponent as ExclamationTriangleOutlined } from "shared/assets/images/exclamation-triangle.svg";
import { getLocaleDatePickerByI18n } from "shared/helpers/getLocaleDatePickerByI18n";
import { StepProps } from "./StepProps";
import { useTranslation } from "react-i18next";
import { LinguagemEnum } from "shared/enums/LinguagemEnum";
import { useSelector } from "react-redux";
import { GenresState } from "explorer/reducers/generos";

const SecondStep: React.FC<StepProps> = () => {
  const { t, i18n } = useTranslation();
  const { genreOptions } = useSelector((state: GenresState) => state.genres);
  const form = Form.useFormInstance();
  const type = Form.useWatch("type", form);
  const releaseOn = Form.useWatch("releaseOn", form);
  const genreSelected = Form.useWatch("genres", form);

  const isReleaseOnInvalid = () => {
    if (!releaseOn) return false;

    return !releaseOn?.isAfter(moment().add(20, "days"));
  };

  const onChangeType = (ev: RadioChangeEvent) => {
    if (ev?.target?.value === ReleaseTypeEnum.Single)
      form?.setFieldValue("focusTrack", null);
  };

  return (
    <div>
      <Form.Item
        name="type"
        label={t("pages.Pitching.steps.second.type")}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Radio.Group onChange={onChangeType}>
          <Space direction="vertical">
            <Radio value={ReleaseTypeEnum.Single}>Single</Radio>
            <Radio value={ReleaseTypeEnum.EP}>EP</Radio>
            <Radio value={ReleaseTypeEnum.Album}>
              {t("pages.Pitching.steps.second.album")}
            </Radio>
          </Space>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="name"
        label={t("pages.Pitching.steps.second.name")}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        hidden={type === ReleaseTypeEnum.Single}
        name="focusTrack"
        label={t("pages.Pitching.steps.second.focusTrack")}
        rules={[
          {
            required: type !== ReleaseTypeEnum.Single,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={t("pages.Pitching.steps.second.genres")}
        name="genres"
        colon={false}
        rules={[
          {
            required: true,
            message: t("quiz.pages.Questionario.genreRequired"),
          },
        ]}
      >
        <Select
          showSearch
          options={genreOptions}
          optionFilterProp="label"
          placeholder={t("quiz.pages.Questionario.selectHerePlaceholder")}
        />
      </Form.Item>

      {genreSelected && genreSelected === "outro" && (
        <Form.Item
          name="genresOthersName"
          label={t("pages.Pitching.steps.second.genreOther")}
          rules={[
            {
              required: true,
              message: t("quiz.pages.Questionario.genreRequired"),
            },
          ]}
        >
          <Input value={genreSelected} />
        </Form.Item>
      )}

      <Form.Item
        name="releaseOn"
        label={t("pages.Pitching.steps.second.releaseOn")}
        rules={[
          {
            required: true,
          },
          {
            validator() {
              if (isReleaseOnInvalid()) {
                return Promise.reject();
              } else {
                return Promise.resolve();
              }
            },
          },
        ]}
      >
        <DatePicker
          locale={getLocaleDatePickerByI18n(i18n.language)}
          style={{ width: "100%" }}
          placeholder={
            i18n?.language === LinguagemEnum.Portugues
              ? "dd/mm/aaaa"
              : "mm/dd/yyyy"
          }
          format={
            i18n?.language === LinguagemEnum.Portugues
              ? "DD/MM/YYYY"
              : "MM/DD/YYYY"
          }
        />
      </Form.Item>
      {isReleaseOnInvalid() && (
        <div className="pending-info">
          <span className="col-icon">
            <ExclamationTriangleOutlined />
          </span>
          <span className="col-text">
            <p className="title">
              {t("pages.Pitching.steps.second.releaseOnInvalid")}
            </p>
            <p>{t("pages.Pitching.steps.second.releaseOnInvalidMsg")}</p>
          </span>
        </div>
      )}
      <Form.Item
        name="labelName"
        label={t("pages.Pitching.steps.second.labelName")}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="feats"
        label={t("pages.Pitching.steps.second.feats")}
        style={{ marginBottom: 0 }}
      >
        <Input />
      </Form.Item>
      <span className="info-small-text">{t("pages.Pitching.opcional")}</span>
    </div>
  );
};

export default SecondStep;
